import { Tooltip } from "antd";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import LoadingSpin from "react-loading-spin";

import { Notification } from "../../../../../../@types/Notification";
import { Close } from "../../../../../../assets/icons/Close";
import { CustomIconButton } from "../../../../../../components/CustomIconButton";
import { SkeletonLoading } from "../../../../../../components/Skeleton";
import {
  useCloseThread,
  useMarkThreadRead,
  useSendThreadMessage,
} from "../../../../../../hooks/mutations/messages";
import { useMessageThread } from "../../../../../../hooks/queries/messages";
import { useModal } from "../../../../../../hooks/useModal";
import { mixpanelActions } from "../../../../../../utils/mixpanel";
import { TooltipText } from "../../PatientMessagesTable.style";
import { CreateMessageModal } from "../CreateMessageModal";
import { ThreadBubble } from "./components/ThreadBubble";
import { ThreadComposer } from "./components/ThreadComposer";
import {
  CloseThreadLink,
  CloseThreadRow,
  Container,
  EmptyState,
  HeaderClose,
  SendingIndicator,
  ThreadClosedText,
  ThreadLayout,
} from "./MessageThreadModal.styles";

type MessageThreadModalProps = {
  /** A thread id, or the id of any message in it. */
  threadId: string;
  patientId: number;
  patientPhoneNumber: string | undefined;
  hasRegisteredDevices: boolean;
  /** EN-5124: replying to an archived patient is rejected by the API (400 PatientArchived). */
  isPatientArchived?: boolean;
  /**
   * Raises a toast WITHOUT closing the modal. The ticket is explicit that closing a thread and
   * sending a custom message both confirm in place and leave the conversation open.
   */
  showToast(
    message: string,
    type: "success" | "warning" | "error" | "info"
  ): void;
  onClose(notification?: Notification, refetchTable?: boolean): void;
};

/**
 * EN-3040. The conversation view, and the composer's "Custom" detour.
 *
 * The step state lives here rather than in two `openModal` calls because ModalContext holds exactly
 * one node and replaces it — re-opening would unmount this component and take the staff member's
 * half-written draft with it. Keeping both steps inside one mounted component is what lets Cancel
 * come back to the thread with the text intact.
 */
export function MessageThreadModal({
  threadId,
  patientId,
  patientPhoneNumber,
  hasRegisteredDevices,
  isPatientArchived = false,
  showToast,
  onClose,
}: MessageThreadModalProps) {
  const { t } = useTranslation();

  const [step, setStep] = useState<"thread" | "custom">("thread");
  const [draft, setDraft] = useState("");
  const [isClosing, setIsClosing] = useState(false);
  // Between a send succeeding and the refetch bringing the new message in.
  const [isRefreshingAfterSend, setIsRefreshingAfterSend] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const hasMarkedRead = useRef(false);

  const { thread, isFetchedAfterMount, refetch } = useMessageThread(threadId);

  const refetchAfterSend = async () => {
    setIsRefreshingAfterSend(true);
    try {
      await refetch();
    } finally {
      setIsRefreshingAfterSend(false);
    }
  };
  const { mutateAsync: sendMessage, isPending: isSending } =
    useSendThreadMessage();
  const { mutateAsync: closeThread } = useCloseThread();
  const { mutate: markThreadRead } = useMarkThreadRead();
  const { updateModalOptions } = useModal();

  // The Custom step is the same CreateMessageModal as "New message", so it gets the same modal size;
  // back on the conversation the thread's own size returns. Updating rather than re-opening keeps
  // this component mounted, and with it the draft. Both updates happen in the same handler so React
  // batches them into one render — an effect would paint the new step at the old size first.
  const goToStep = (next: "thread" | "custom") => {
    updateModalOptions(
      next === "custom"
        ? { width: "auto", height: "auto", maxHeight: undefined }
        : { width: "756px", height: "100%", maxHeight: "873px" }
    );
    setStep(next);
  };

  // Opening the conversation is what "the care team has seen this" means, so the unread count and
  // the row highlight clear together.
  useEffect(() => {
    if (!thread || hasMarkedRead.current) return;
    if (thread.unreadForStaffCount === 0) return;

    hasMarkedRead.current = true;
    markThreadRead(thread.threadId);
  }, [thread]);

  // A conversation is read bottom-up: land on the newest turn.
  useEffect(() => {
    const scrollable = containerRef.current;
    if (!scrollable || step !== "thread") return;

    scrollable.scrollTo({ top: scrollable.scrollHeight, behavior: "smooth" });
  }, [thread?.turns.length, step, isRefreshingAfterSend]);

  const handleSend = async () => {
    if (!thread || !draft.trim()) return;

    const wasClosed = thread.isClosed;
    try {
      await sendMessage({ threadId: thread.threadId, body: draft.trim() });
    } catch {
      // The draft stays, so nothing the staff member wrote is lost.
      showToast(t("patientPage_messages_view_responseFailed"), "error");
      return;
    }

    mixpanelActions.track(
      wasClosed ? "User Action: Send & reopen" : "User Action: Send Message"
    );

    setDraft("");
    await refetchAfterSend();
  };

  const handleCloseThread = async () => {
    if (!thread || isClosing) return;

    // Held until the refetch lands, so the link does not come back as clickable for the moment between
    // the close succeeding and the closed state arriving.
    setIsClosing(true);
    try {
      await closeThread(thread.threadId);
      mixpanelActions.track("User Action: Close thread");

      // The modal deliberately stays open: the ticket wants the closed state visible in place, with
      // the link turning into plain text and the Send CTA into "Send & reopen".
      showToast(t("patientPage_messages_thread_closedToast"), "success");
      await refetch();
    } catch {
      showToast(t("patientPage_messages_thread_closeFailed"), "error");
    } finally {
      setIsClosing(false);
    }
  };

  if (step === "custom" && thread) {
    return (
      <>
        <HeaderClose
          type="button"
          aria-label={t("patientPage_messages_thread_backToConversation")}
          onClick={() => goToStep("thread")}
        >
          <CustomIconButton>
            <Close />
          </CustomIconButton>
        </HeaderClose>

        <CreateMessageModal
          patientId={patientId}
          patientPhoneNumber={patientPhoneNumber}
          hasRegisteredDevices={hasRegisteredDevices}
          threadId={thread.threadId}
          initialTitle={thread.subject}
          initialBody={draft}
          // Cancel and the header X both come back to the conversation rather than dropping the
          // staff member out of the flow.
          onCancel={() => goToStep("thread")}
          onSent={async () => {
            setDraft("");
            goToStep("thread");
            await refetchAfterSend();
          }}
          setNotification={showToast}
          onClose={() => goToStep("thread")}
        />
      </>
    );
  }

  // Until the fetch made on opening lands, a cached thread would show stale read states.
  if (!isFetchedAfterMount) {
    return (
      <Container>
        <SkeletonLoading width="100%" height="120px" />
        <SkeletonLoading width="100%" height="120px" />
      </Container>
    );
  }

  if (!thread) {
    return (
      <EmptyState>{t("patientPage_messages_thread_loadError")}</EmptyState>
    );
  }

  // A "No answer" message is an announcement: the bar stays, disabled with its reason, the way the
  // single-message modal always showed a conversation that could not continue.
  const acceptsReplies = thread.acceptsReplies !== false;

  let disabledReason: string | undefined;
  if (isPatientArchived) {
    disabledReason = t("patientPage_messages_view_archivedPatient");
  } else if (!acceptsReplies) {
    disabledReason = t("patientPage_messages_thread_noAnswerNotReplyable");
  }

  // A patient turn sharing its messageId with a staff options question is the pick left inline on it.
  const optionQuestionIds = new Set(
    thread.turns
      .filter(
        (turn) => turn.author === "Staff" && turn.responseType === "Options"
      )
      .map((turn) => turn.messageId)
  );

  return (
    <>
      <HeaderClose
        type="button"
        aria-label={t("patientPage_messages_thread_close")}
        onClick={() => onClose()}
      >
        <CustomIconButton>
          <Close />
        </CustomIconButton>
      </HeaderClose>

      <ThreadLayout>
        <Container ref={containerRef}>
          {thread.turns.map((turn, index) => (
            <ThreadBubble
              key={`${turn.messageId}-${turn.author}-${turn.timestamp}`}
              turn={turn}
              subject={index === 0 ? thread.subject : undefined}
              isAnsweredOption={
                turn.author === "Patient" &&
                optionQuestionIds.has(turn.messageId)
              }
            />
          ))}

          {isRefreshingAfterSend && (
            <SendingIndicator
              aria-label={t("patientPage_messages_thread_updating")}
            >
              {/* Same spinner and colour as the tables' "load more". */}
              <LoadingSpin
                size="24px"
                width="3px"
                primaryColor="var(--colors-portal-loading-state-color)"
                secondaryColor="#E8E8E8"
              />
            </SendingIndicator>
          )}

          {acceptsReplies && (
            <CloseThreadRow>
              {thread.isClosed ? (
                <ThreadClosedText>
                  {t("patientPage_messages_thread_closed")}
                </ThreadClosedText>
              ) : (
                <Tooltip
                  title={
                    <TooltipText>
                      {t("patientPage_messages_thread_closeTooltip")}
                    </TooltipText>
                  }
                  zIndex={3002}
                  color="#4B4B4B"
                  placement="top"
                  arrow={false}
                  styles={{
                    body: {
                      padding: "6px 12px",
                      minHeight: "40px",
                      width: "max-content",
                      borderRadius: "4px !important",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontFamily: "'Open Sans', sans-serif",
                    },
                  }}
                >
                  <CloseThreadLink
                    type="button"
                    onClick={handleCloseThread}
                    disabled={isClosing}
                    aria-busy={isClosing}
                  >
                    {isClosing
                      ? t("patientPage_messages_thread_closing")
                      : t("patientPage_messages_thread_closeThisThread")}
                  </CloseThreadLink>
                </Tooltip>
              )}
            </CloseThreadRow>
          )}
        </Container>

        <ThreadComposer
          draft={draft}
          onDraftChange={setDraft}
          onSend={handleSend}
          onCustom={() => goToStep("custom")}
          isSending={isSending}
          isClosed={thread.isClosed}
          disabledReason={disabledReason}
          isBusy={isClosing || isRefreshingAfterSend}
          isCustomAvailable={hasRegisteredDevices}
        />
      </ThreadLayout>
    </>
  );
}
